import React, { useEffect, useMemo, useState } from "react";
import { Mic2, Radio, Music2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${BACKEND}${u}`;
  return u;
};

const gradientForName = (name) => {
  // Deterministic warm gradient (gold/crimson palette) from name hash
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const palettes = [
    ["#7a1420", "#D4AF37"],
    ["#5A0F1A", "#EAD385"],
    ["#997A15", "#7a1420"],
    ["#D4AF37", "#5A0F1A"],
    ["#3a0a10", "#D4AF37"],
    ["#997A15", "#EAD385"],
  ];
  const p = palettes[Math.abs(hash) % palettes.length];
  return `linear-gradient(135deg, ${p[0]} 0%, ${p[1]} 100%)`;
};

const initials = (name) =>
  name
    .replace(/^dj\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "GFN";

const DjBios = () => {
  const [shows, setShows] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [sRes, pRes] = await Promise.all([
          fetch(`${BACKEND}/api/shows`),
          fetch(`${BACKEND}/api/djs`),
        ]);
        if (sRes.ok && alive) setShows(await sRes.json());
        if (pRes.ok && alive) setProfiles(await pRes.json());
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const djs = useMemo(() => {
    const map = new Map();
    // Seed from shows (existing behavior)
    shows.forEach((s) => {
      const dj = (s.dj || "").trim();
      if (!dj) return;
      if (!map.has(dj)) map.set(dj, { name: dj, shows: [] });
      map.get(dj).shows.push(s.name);
    });
    // Also include any DJs with a profile even if they have no shows yet
    profiles.forEach((p) => {
      if (!map.has(p.name)) map.set(p.name, { name: p.name, shows: [] });
      const entry = map.get(p.name);
      entry.image_url = p.image_url || null;
      entry.bio = p.bio || null;
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [shows, profiles]);

  if (loading) return null;
  if (djs.length === 0) return null;

  return (
    <section
      data-testid="dj-bios"
      className="mb-14"
    >
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-1.5 backdrop-blur-sm bg-black/40">
            <Mic2 className="w-3.5 h-3.5 text-gold" />
            <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
              Meet the Voices
            </span>
          </div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl uppercase text-white">
            Behind the <span className="text-gold-gradient">mic</span>
          </h2>
          <p className="mt-2 font-body text-neutral-400 text-sm md:text-base max-w-2xl">
            The DJs, hosts, and storytellers who bring GFN Soul NYC to life every day.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {djs.map((dj) => (
          <article
            key={dj.name}
            data-testid={`dj-card-${dj.name.replace(/\s+/g, "-").toLowerCase()}`}
            className="group relative rounded-lg overflow-hidden bg-jet-surface border border-gold/25 hover:border-gold/60 transition-colors p-5"
          >
            <div className="flex items-center gap-4">
              <div
                className="relative w-16 h-16 shrink-0 rounded-full grid place-items-center font-display text-2xl text-black shadow-[0_0_18px_rgba(212,175,55,0.35)] ring-1 ring-gold/40 overflow-hidden"
                style={{ background: gradientForName(dj.name) }}
                aria-hidden
              >
                {dj.image_url ? (
                  <img
                    src={resolveImg(dj.image_url)}
                    alt={dj.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="drop-shadow-sm">{initials(dj.name)}</span>
                )}
                <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-black border border-gold/60 grid place-items-center">
                  <Radio className="w-3 h-3 text-gold" />
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-2xl text-white uppercase leading-tight">
                  {dj.name}
                </div>
                <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-gold mt-1">
                  On-Air Host
                </div>
              </div>
            </div>

            {dj.bio && (
              <p className="mt-3 font-body text-sm text-neutral-300 leading-relaxed">
                {dj.bio}
              </p>
            )}

            <div className="mt-4">
              <div className="font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1.5">
                Hosts
              </div>
              <ul className="space-y-1">
                {dj.shows.slice(0, 4).map((sh) => (
                  <li
                    key={sh}
                    className="flex items-start gap-2 text-sm text-neutral-200"
                  >
                    <Music2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gold/80" />
                    <span className="font-body">{sh}</span>
                  </li>
                ))}
                {dj.shows.length > 4 && (
                  <li className="text-xs text-neutral-500 pl-6">
                    +{dj.shows.length - 4} more
                  </li>
                )}
              </ul>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default DjBios;
